import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Table, Space, PageHeader, Button, Modal, notification } from "antd";
//import { reverse } from "named-urls";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { companyBillingAddressSaga } from "./saga";
import * as selectors from "./selectors";
import { addressToString } from "../../../../utils/formatter";
import addressSchema from "../../../../schemas/addressSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../contexts/auth";

import AddAddressForm from "../../../../forms/AddAddressForm";

const { Column } = Table;
const STORAGE_KEY = 'companyBillingAddressFilters';

const CompanyBillingAddressListing = () => {
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: companyBillingAddressSaga });
  const dispatch = useDispatch();
  const { companyId } = useParams();

  const companyDetails = useSelector(selectors.selectCompanyDetails) || {};
  const countries = useSelector(selectors.selectCountries);
  const states = useSelector(selectors.selectStates);
  const cities = useSelector(selectors.selectCities);
  const billingAddress =
    useSelector(selectors.selectCompanyBillingAddreses) || [];
  const totalBillingAddress = useSelector(selectors.selectTotalAddresses) || [];

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsedState = JSON.parse(saved);
        // Validate that saved state matches current company context
        if (parsedState.companyId === companyId) {
          return parsedState;
        }
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    
    return {
      currentPage: 1,
      filters: {},
      companyId: companyId,
    };
  };

  const initialState = getInitialState();

  const [addBillingAddressModalVisbile, setAddBillingAddressModalVisible] =
    useState(false);
  const [editBillingAddressModalVisible, setEditBillingAddressModalVisible] =
    useState(false);
  const [addressForEdit, setAddressForEdit] = useState({});
  const [triggerReload, setTriggerReload] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [filters, setFilters] = useState(initialState.filters);
  useEffect(() => {
    const state = {
      currentPage,
      filters,
      companyId: companyId,
    };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, filters, companyId]);

  useEffect(() => {
    let query = `page=${currentPage}&limit=10`;
    
    if (filters.address && filters.address.length > 0) {
      let addresses = "";
      let count = 0;
      filters.address.forEach((add) => {
        addresses = addresses + add.split(",")[0];
        count = count + 1;
        if (filters.address.length !== count) addresses = addresses + ",";
      });
      query = `addressLine1=${addresses}&search=true`;
    }

    dispatch(actions.fetchCompanyBillingAddress({ id: companyId, query }));
    dispatch(actions.fetchTotalBillingAddresses({ id: companyId }));
    dispatch(actions.fetchCompanyDetails({ id: companyId }));
    dispatch(actions.fetchCountries());
    
    return () => {
      dispatch(actions.initializeState());
    };
  }, [dispatch, companyId, triggerReload, currentPage, filters]);

  const tableData = [
    ...[...billingAddress].map((add) => ({
      formattedAddress: addressToString(add),
      address: {
        id: add?._id,
        addressLine1: add?.addressLine1,
        addressLine2: add?.addressLine2,
        city: add?.city?._id || "",
        country: add?.country?._id || "",
        state: add?.state?._id || "",
        pincode: add?.pincode || "",
      },
    })),
  ];

  const handleTablePagination = (pagination, filters, sorter) => {
    setCurrentPage(pagination.current);
    setFilters({
      address: filters.address || null,
    });
    let query = `page=${pagination.current}&limit=10`;

    if (filters.address) {
      let addresses = "";
      let count = 0;
      filters.address.forEach((add) => {
        addresses = addresses + add.split(",")[0];
        count = count + 1;
        if (filters.address.length !== count) addresses = addresses + ",";
      });

      query = `addressLine1=${addresses}&search=true`;
    }

    dispatch(actions.fetchCompanyBillingAddress({ id: companyId, query }));
  };
  const resetAddress = () => {
    reset({
      type: "billing",
      addressLine1: "",
      addressLine2: "",
      district: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      landmark: "",
    });
  };
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: "billing",
      addressLine1: "",
      addressLine2: "",
      district: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      landmark: "",
    },
    resolver: yupResolver(addressSchema),
  });

  const selectedCountry = watch("country");
  const selectedState = watch("state");

  useEffect(() => {
    if (selectedCountry) {
      dispatch(actions.fetchStates({ countryId: selectedCountry }));
    }
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedState)
      dispatch(actions.fetchCities({ stateId: selectedState }));
  }, [selectedState, dispatch]);

  useEffect(() => {
    if (editBillingAddressModalVisible) {
      reset(addressForEdit.address);
    } else {
      reset({
        type: "billing",
        addressLine1: "",
        addressLine2: "",
        district: "",
        city: "",
        state: "",
        country: "",
        pincode: "",
        landmark: "",
      });
    }
  }, [reset, editBillingAddressModalVisible, addressForEdit]);

  const onSubmitAddBillingAddress = (data) => {
    data.company = companyDetails._id;
    setAddBillingAddressModalVisible(false);
    dispatch(
      actions.addBillingAddress({
        data,
        onSuccess: () => {
          notification.success({
            message: "Billing Address added successfully!!!",
          });
          setTriggerReload(!triggerReload);
        },
        onFailure: () => {
          notification.error({
            message: "There was some error while adding Billing Address",
          });
        },
      })
    );
    resetAddress();
  };

  const onSubmitEditBillingAddress = (data) => {
    data.company = companyDetails._id;
    setEditBillingAddressModalVisible(false);
    dispatch(
      actions.updateBillingAddress({
        data: data,
        id: addressForEdit.address.id,
        onSuccess: () => {
          notification.success({
            message: "Billing Address updated successfully!!!",
          });
          setTriggerReload(!triggerReload);
        },
        onFailure: () => {
          notification.error({
            message: "There was some error while updating Billing Address",
          });
        },
      })
    );
  };

  const handleAddCancel = () => {
    setAddBillingAddressModalVisible(false);
    resetAddress();
  };
  const handleEditCancel = () => {
    setEditBillingAddressModalVisible(false);
  };

  const getAddressFilters = () => {
    if (!totalBillingAddress?.length) return [];
    const filter = totalBillingAddress.map((add) => ({
      text: addressToString(add),
      value: addressToString(add),
    }));
    return filter;
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader
        ghost={false}
        title="Billing Addresses"
        subTitle={companyDetails.companyName}
        extra={[
          userRole !== "support_team" && (
            <Button
              key="1"
              type="primary"
              onClick={() => {
                setAddBillingAddressModalVisible(true);
              }}
            >
              Add Billing Address
            </Button>
          )
        ]}
      />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          dataSource={tableData}
          pagination={{
            current: currentPage,
            pageSize: 10,
            showSizeChanger: false,
            disabled: tableData.length === 0 ? true : false,
          }}
          onChange={handleTablePagination}
        >
          <Column
            title="Address"
            dataIndex="formattedAddress"
            key="address"
            filters={getAddressFilters()}
            filteredValue={filters.address || null}
            filterSearch={true}
          />
          {userRole !== "support_team" && (

            <Column
              title="Action"
              key="action"
              render={(text, record) => (
                <Space size="middle">
                  <Button
                    onClick={() => {
                      setAddressForEdit(record);
                      setEditBillingAddressModalVisible(true);
                    }}
                  >
                    Edit
                  </Button>
                </Space>
              )}
            />
          )}
        </Table>
      </div>
      <Modal
        title="Add Billing Address"
        visible={addBillingAddressModalVisbile}
        centered
        onOk={handleSubmit(onSubmitAddBillingAddress)}
        onCancel={handleAddCancel}
        okText="Save"
        check={false}
      >
        <AddAddressForm
          errors={errors}
          control={control}
          watch={watch}
          options={{ countries, states, cities }}
          check={false}
          setValue={setValue}
        />
      </Modal>
      <Modal
        title="Edit Billing Address"
        visible={editBillingAddressModalVisible}
        centered
        onOk={handleSubmit(onSubmitEditBillingAddress)}
        onCancel={handleEditCancel}
        okText="Save"
        check={false}
      >
        <AddAddressForm
          errors={errors}
          control={control}
          watch={watch}
          options={{ countries, states, cities }}
          check={false}
          setValue={setValue}
        />
      </Modal>
    </div>
  );
};

export default CompanyBillingAddressListing;